import { type PagyMeta, PagyPagination } from "@/components/pagy-pagination"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, usePage } from "@inertiajs/react"
import { IconCircleCheck, IconCircleDashed, IconCircleX, IconClock, IconPlus } from "@tabler/icons-react"

interface User {
  id: number
  name: string
  avatar_url: string | null
}

interface Cycle {
  id: number
  display_name: string
  status: string
  cycle_period: string
  year: number
  start_date: string | null
  end_date: string | null
  overall_score: number | null
  user: User
  review_count: number
  completed_reviews: number
}

interface Props {
  [key: string]: unknown
  cycles: Cycle[]
  pagy: PagyMeta
  can_create: boolean
}

function statusIcon(status: string) {
  switch (status) {
    case "completed":
      return <IconCircleCheck className="h-4 w-4 text-green-600" />
    case "in_progress":
    case "reviews_completed":
    case "admin_review":
      return <IconClock className="h-4 w-4 text-yellow-600" />
    case "cancelled":
      return <IconCircleX className="h-4 w-4 text-red-600" />
    default:
      return <IconCircleDashed className="h-4 w-4 text-muted-foreground" />
  }
}

function statusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "completed":
      return "default"
    case "in_progress":
    case "reviews_completed":
    case "admin_review":
      return "secondary"
    case "cancelled":
      return "destructive"
    default:
      return "outline"
  }
}

function statusLabel(status: string) {
  return status
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ")
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export default function PerformanceReviewCyclesIndex() {
  const { cycles, pagy, can_create } = usePage<Props>().props

  return (
    <>
      <Head title="Performance Review Cycles - Operations Center" />
      <div className="flex flex-1 flex-col gap-4 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Performance Review Cycles</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold">Performance Review Cycles</h1>
          {can_create && (
            <Button size="sm" asChild>
              <a href="/performance_review_cycles/new">
                <IconPlus className="mr-1 h-4 w-4" />
                Create New Cycle
              </a>
            </Button>
          )}
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Cycle</TableHead>
              <TableHead>Employee</TableHead>
              <TableHead>Date Range</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reviews</TableHead>
              <TableHead>Score</TableHead>
              <TableHead className="w-10">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycles.map((cycle) => (
              <TableRow key={cycle.id}>
                <TableCell>{statusIcon(cycle.status)}</TableCell>
                <TableCell>
                  <a
                    href={`/performance_review_cycles/${cycle.id}`}
                    className="font-medium text-primary hover:underline"
                  >
                    {cycle.display_name}
                  </a>
                </TableCell>
                <TableCell className="text-muted-foreground">{cycle.user.name}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {formatDate(cycle.start_date)} – {formatDate(cycle.end_date)}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(cycle.status)}>{statusLabel(cycle.status)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {cycle.completed_reviews}/{cycle.review_count}
                </TableCell>
                <TableCell>
                  {cycle.overall_score ? (
                    <span className="font-medium">{cycle.overall_score.toFixed(1)}</span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" asChild>
                    <a href={`/performance_review_cycles/${cycle.id}`}>View</a>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {cycles.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-muted-foreground text-center py-8">
                  No performance review cycles found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <PagyPagination pagy={pagy} />
      </div>
    </>
  )
}

PerformanceReviewCyclesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
